// kontextFold — which group headings of the Kontext panel's home rest folded
// (Mål, Material i den här chatten, Källor och var de används, Övriga filer),
// remembered per device (owner 2026-10-06: fold away what you are not working
// on right now). Device display chrome like the dock and panel widths, never a
// domain fact (project_theme_outside_event_log): its own localStorage key, no
// event. Only the impure host reads/writes; parse/serialize are pure.
//
// Parse, don't validate: the stored string may come from any earlier build. A
// group never toggled falls back to its default (only "Övriga filer" starts
// folded, as before); unknown keys and non-booleans are dropped.

import { readDeviceKey, writeDeviceKey } from "./deviceStore.js";

export type KontextGroup = "goal" | "materials" | "sources" | "others";

export type KontextFold = Readonly<Partial<Record<KontextGroup, boolean>>>;

const GROUPS: readonly KontextGroup[] = [
    "goal",
    "materials",
    "sources",
    "others",
];

const DEFAULT_FOLDED: Record<KontextGroup, boolean> = {
    goal: false,
    materials: false,
    sources: false,
    others: true,
};

export const isFolded = (fold: KontextFold, group: KontextGroup): boolean =>
    fold[group] ?? DEFAULT_FOLDED[group];

export const toggled = (
    fold: KontextFold,
    group: KontextGroup,
): KontextFold => ({ ...fold, [group]: !isFolded(fold, group) });

export const parseKontextFold = (raw: string | null): KontextFold => {
    if (raw === null) return {};
    let data: unknown;
    try {
        data = JSON.parse(raw);
    } catch {
        return {};
    }
    if (typeof data !== "object" || data === null || Array.isArray(data))
        return {};
    const entries = new Map<string, unknown>(Object.entries(data));
    return Object.fromEntries(
        GROUPS.flatMap((g) => {
            const v = entries.get(g);
            return typeof v === "boolean" ? [[g, v]] : [];
        }),
    );
};

export const serializeKontextFold = (fold: KontextFold): string =>
    JSON.stringify(fold);

const STORAGE_KEY = "bilda.kontextFold";

export const readKontextFold = (): KontextFold =>
    parseKontextFold(readDeviceKey(STORAGE_KEY));

export const writeKontextFold = (fold: KontextFold): void =>
    writeDeviceKey(STORAGE_KEY, serializeKontextFold(fold));
